import { Button, Link, Stack, Tab, TableCell, TableRow, Tabs, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { config } from "../../../../config";
import { useApiServiceCallback } from "../../../../server/api/client";
import { EmailEngineSevice } from "../../../../server/api/email-engine-v2";
import { useSupabaseLoader } from "../../../../server/supabase/hooks";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { useEnabledChannels } from "../../../channels/hooks/use-enabled-channels";
import { PaginatedTable } from "../../../generic/components/paginated-table/paginated-table";
import { SupabaseOptedOutService } from "../../server/supabase-opted-out-service";

import type { OptedOut } from "../../server/supabase-opted-out-service";
import type { FC } from "react";

type Channels = "personal_email" | "twilio" | "teams" | "whatsapp" | "work_email";

const tableFields: Record<Channels, { labels: string[]; values: (keyof OptedOut)[] }> = {
  personal_email: {
    labels: ["First Name", "Last Name", "Personal Email"],
    values: ["first_name", "last_name", "destination"],
  },
  twilio: {
    labels: ["First Name", "Last Name", "Phone Number", "Work Email"],
    values: ["first_name", "last_name", "destination", "work_email"],
  },
  teams: {
    labels: ["First Name", "Last Name", "Work Email"],
    values: ["first_name", "last_name", "work_email"],
  },
  whatsapp: {
    labels: ["First Name", "Last Name", "Phone Number", "Work Email"],
    values: ["first_name", "last_name", "destination", "work_email"],
  },
  work_email: {
    labels: ["First Name", "Last Name", "Work Email"],
    values: ["first_name", "last_name", "work_email"],
  },
};

const constructChannelHeader = (
  channel: Channels,
  optedOutData: OptedOut[],
  onSyncSuppressionList?: () => Promise<void>,
  syncing?: boolean,
): React.ReactNode => {
  switch (channel) {
    case "personal_email":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: "center", justifyContent: "space-between" }}
          >
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <ChannelIcon channel="personal_email" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
                Personal Email
              </Typography>
            </Stack>
            <Button
              data-analytics-id="opt-outs-sync-personal-email"
              variant="outlined"
              size="small"
              loading={syncing}
              onClick={() => void onSyncSuppressionList?.().catch(console.error)}
            >
              Sync Opt-Outs
            </Button>
          </Stack>
          {optedOutData.length > 0 ? (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              These people have blocked Communications to their personal email from your
              ChangeEngine account. We’re aware and will remove these blocks within the next 7 days.
              If you have any questions, please contact{" "}
              <Link href="mailto:support@changeengine.com">support@changeengine.com</Link>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone blocking messages to their personal email.
            </Typography>
          )}
        </Stack>
      );
    case "teams":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="teams" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              Teams
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              These people have blocked the ChangeEngine Teams bot. We’ll continue sending
              Communications, as we can’t tell they’ve unblocked the bot until we successfully send
              them a Communication.
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone blocking messages to their Teams.
            </Typography>
          )}
        </Stack>
      );
    case "twilio":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="twilio" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              SMS
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              These people have replied “STOP” to one of your SMS Communications.
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone blocking messages to their SMS.
            </Typography>
          )}
        </Stack>
      );
    case "whatsapp":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack direction="row" sx={{ gap: 1 }}>
            <ChannelIcon channel="whatsapp" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
              WhatsApp
            </Typography>
          </Stack>
          {optedOutData.length > 0 ? (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              These people have replied “STOP” to one of your SMS Communications.
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone blocking messages to their WhatsApp.
            </Typography>
          )}
        </Stack>
      );
    case "work_email":
      return (
        <Stack sx={{ gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: "center", justifyContent: "space-between" }}
          >
            <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
              <ChannelIcon channel="work_email" />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
                Work Email
              </Typography>
            </Stack>
            <Button
              data-analytics-id="opt-outs-sync-work-email"
              variant="outlined"
              size="small"
              loading={syncing}
              onClick={() => void onSyncSuppressionList?.().catch(console.error)}
            >
              Sync Opt-Outs
            </Button>
          </Stack>
          {optedOutData.length > 0 ? (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              These people have blocked Communications to their work email from your ChangeEngine
              account. We’re aware and will remove these blocks within the next 7 days. If you have
              any questions, please contact{" "}
              <Link href="mailto:support@changeengine.com">support@changeengine.com</Link>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
              We’re not aware of anyone blocking messages to their work email.
            </Typography>
          )}
        </Stack>
      );
    default:
      return <></>;
  }
};

const constructTable = (channel: Channels, optedOutData: OptedOut[]): React.ReactNode => {
  const channelId = config.channel[channel];
  const { labels, values } = tableFields[channel];

  return (
    <PaginatedTable
      config={{
        headers: labels,
      }}
      data={optedOutData.filter((d) => d.channel_id === channelId)}
      renderRow={(optOut: OptedOut) => (
        <TableRow data-testid="opted-out-grid-row">
          {values.map((value) => (
            <TableCell key={value}>{optOut[value]}</TableCell>
          ))}
        </TableRow>
      )}
    />
  );
};

const ALL_OPT_OUT_CHANNELS: { key: Channels; label: string }[] = [
  { key: "personal_email", label: "Personal Email" },
  { key: "twilio", label: "SMS" },
  { key: "teams", label: "Teams" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "work_email", label: "Work Email" },
];

export const OptOuts: FC = () => {
  const { data: enabledChannels, loading: enabledChannelsLoading } = useEnabledChannels();

  const { data: optedOutData, loading: optedOutLoading } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data, error } = await new SupabaseOptedOutService(supabase).getAll(account_id, {
        order: [{ column: "last_name" }],
      });
      if (error) throw error;
      if (!data) throw new Error("No opted-out data found");
      return data;
    },
    [],
  );

  const availableChannels = useMemo(() => {
    if (!enabledChannels) return [];
    return ALL_OPT_OUT_CHANNELS.filter(({ key }) => enabledChannels.includes(key));
  }, [enabledChannels]);

  const [displayedTab, setDisplayedTab] = useState<Channels>("twilio");
  const [syncing, setSyncing] = useState(false);

  const handleSyncSuppressionList = useApiServiceCallback(
    EmailEngineSevice,
    async ({ apiService }) => {
      setSyncing(true);
      try {
        const { error } = await apiService.updateSuppressionList();
        if (error) {
          toast.error("Failed to request opt-out sync. Please try again.");
        } else {
          toast.success("Sync requested. Please check back in a few minutes for updated records.");
        }
      } catch {
        toast.error("Failed to request opt-out sync. Please try again.");
      } finally {
        setSyncing(false);
      }
    },
    [],
  );

  // Fall back to the first available channel if the selected tab is not accessible
  const activeTab = useMemo(() => {
    const keys = availableChannels.map((c) => c.key);
    return keys.includes(displayedTab) ? displayedTab : keys[0];
  }, [availableChannels, displayedTab]);

  const channelSpecificData = useMemo(() => {
    if (optedOutLoading || !activeTab) return [];
    if (!optedOutData) return [];

    return optedOutData.filter((item) => item.channel_id === config.channel[activeTab]);
  }, [optedOutLoading, optedOutData, activeTab]);

  if (enabledChannelsLoading || !activeTab) return null;

  return (
    <Stack sx={{ gap: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
        Opt-Outs and Blocks
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        Employees can opt out of SMS and WhatsApp Communications, or block emails and the
        ChangeEngine Teams Bot. You can view everyone who has done this below.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: "14px" }}>
        SMS and WhatsApp opt-outs are recorded immediately. Email and Teams blocks are only detected
        when you next send that person a Communication and their email provider or Microsoft Teams
        notifies us that delivery was blocked.
      </Typography>
      {/* oxlint-disable-next-line typescript/no-unsafe-type-assertion */}
      <Tabs value={activeTab} onChange={(e, tab) => setDisplayedTab(tab as Channels)}>
        {availableChannels.map(({ key, label }) => (
          <Tab key={key} label={label} value={key} />
        ))}
      </Tabs>
      {constructChannelHeader(activeTab, channelSpecificData, handleSyncSuppressionList, syncing)}
      {channelSpecificData.length > 0 && constructTable(activeTab, channelSpecificData)}
    </Stack>
  );
};
